<script setup>
/* eslint-disable vue/custom-event-name-casing, vue/no-bare-strings-in-template */
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNow } from '@vueuse/core';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import { formatWhatsappUsername } from 'dashboard/helper/whatsappUsername';

const props = defineProps({
  conversation: { type: Object, required: true },
  columnLabel: { type: [String, null], default: null },
  attentionLabels: { type: Array, default: () => [] },
});
defineEmits(['card-click']);
const { t, locale } = useI18n();
const now = inject('tmkKanbanNow', null) || useNow({ interval: 60_000 });
const dateFormatters = computed(() => {
  const options = {
    timeZone: 'America/Bogota',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  return [
    new Intl.DateTimeFormat(locale.value, options),
    new Intl.DateTimeFormat(locale.value, {
      ...options,
      hour: '2-digit',
      minute: '2-digit',
    }),
  ];
});
const relativeFormatter = computed(
  () =>
    new Intl.RelativeTimeFormat(locale.value, {
      numeric: 'auto',
      style: 'short',
    })
);
const attention = computed(() =>
  ['unattended', 'follow_up'].includes(props.conversation.attention_status)
    ? props.conversation.attention_status
    : null
);
const isUnattended = computed(() => attention.value === 'unattended');
const contact = computed(() => props.conversation.meta?.sender || {});
const contactName = computed(
  () => contact.value.name || t('CHAT_LIST.TMK.UNKNOWN')
);
const assignee = computed(() => props.conversation.meta?.assignee);
const labels = computed(() =>
  (props.conversation.labels || []).filter(
    label =>
      label !== props.columnLabel &&
      (!attention.value || !props.attentionLabels.includes(label))
  )
);
const username = computed(() => {
  return formatWhatsappUsername(
    contact.value.additional_attributes || contact.value.additionalAttributes
  );
});
const parseDate = value => {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(typeof value === 'number' ? value * 1000 : value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const formatDate = (value, includeTime = false) => {
  const date = parseDate(value);
  if (!date) return t('CHAT_LIST.TMK.UNKNOWN');
  return dateFormatters.value[includeTime ? 1 : 0].format(date);
};
const relativeTime = value => {
  const date = parseDate(value);
  if (!date) return '—';
  const seconds = (date.getTime() - now.value.getTime()) / 1000;
  const [, divisor, unit] = [
    [60, 1, 'second'],
    [3600, 60, 'minute'],
    [86400, 3600, 'hour'],
    [Infinity, 86400, 'day'],
  ].find(([limit]) => Math.abs(seconds) < limit);
  return relativeFormatter.value.format(Math.round(seconds / divisor), unit);
};
const message = computed(() => props.conversation.last_non_activity_message);
const preview = computed(
  () =>
    message.value?.content?.slice(0, 280) ||
    (message.value?.attachments?.length
      ? t('CHAT_LIST.TMK.ATTACHMENT')
      : t('CHAT_LIST.NO_MESSAGES'))
);
const unreadCount = computed(() => props.conversation.unread_count || 0);
const onDragStart = event => {
  event.dataTransfer.setData('text/plain', String(props.conversation.id));
  event.dataTransfer.setData(
    'application/x-from-label',
    props.columnLabel ?? ''
  );
};
</script>

<template>
  <div
    data-testid="kanban-card"
    draggable="true"
    role="button"
    tabindex="0"
    class="group flex flex-col gap-3 rounded-xl border border-n-weak bg-n-background p-3.5 shadow-sm cursor-pointer transition-colors hover:border-n-slate-7 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-teal-8"
    @keydown.enter="$emit('card-click', conversation.id)"
    @keydown.space.prevent="$emit('card-click', conversation.id)"
    @click="$emit('card-click', conversation.id)"
    @dragstart="onDragStart"
  >
    <div class="flex items-start gap-2.5">
      <Avatar
        :name="contactName"
        :src="contact.thumbnail || ''"
        :size="32"
        rounded-full
      />
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span
            class="min-w-0 flex-1 truncate text-sm font-semibold text-n-slate-12"
            :title="contactName"
            >{{ contactName }}</span
          >
          <span class="shrink-0 text-[10px] tabular-nums text-n-slate-10"
            >#{{ conversation.id }}</span
          >
        </div>
        <div
          v-if="contact.phone_number"
          class="mt-1 truncate text-xs text-n-slate-11"
          :title="contact.phone_number"
        >
          {{ contact.phone_number }}
        </div>
        <div
          v-if="username"
          class="mt-0.5 truncate text-xs text-n-teal-11"
          :title="username"
        >
          {{ username }}
        </div>
      </div>
    </div>
    <div
      v-if="attention || unreadCount"
      class="flex items-center justify-between gap-2"
    >
      <span
        v-if="attention"
        data-testid="kanban-attention"
        class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium"
        :class="
          isUnattended
            ? 'bg-n-ruby-2 text-n-ruby-11'
            : 'bg-n-teal-2 text-n-teal-11'
        "
      >
        <span
          :class="
            isUnattended ? 'i-lucide-message-circle' : 'i-lucide-check-check'
          "
          class="size-3 shrink-0"
          aria-hidden="true"
        />
        {{
          t(
            isUnattended
              ? 'CHAT_LIST.TMK.UNATTENDED'
              : 'CHAT_LIST.TMK.FOLLOWING_UP'
          )
        }}
      </span>
      <span
        v-if="unreadCount"
        class="ml-auto rounded-full bg-n-slate-3 px-2 py-0.5 text-[10px] tabular-nums text-n-slate-11"
        :title="t('CHAT_LIST.TMK.UNREAD')"
        >{{ unreadCount }}</span
      >
    </div>
    <p
      class="m-0 line-clamp-2 min-h-8 break-words text-xs leading-relaxed text-n-slate-11"
      :title="preview"
    >
      {{ preview }}
    </p>
    <div v-if="labels.length" class="flex min-w-0 flex-wrap gap-1">
      <Label
        v-for="label in labels.slice(0, 2)"
        :key="label"
        :label="label"
        compact
      />
      <span
        v-if="labels.length > 2"
        class="rounded bg-n-slate-3 px-1.5 py-0.5 text-[11px] text-n-slate-11"
        :title="labels.slice(2).join(', ')"
        >+{{ labels.length - 2 }}</span
      >
    </div>
    <div class="flex flex-col gap-2 border-t border-n-weak pt-2.5">
      <div
        class="flex items-center gap-1.5 text-xs text-n-slate-11"
        :title="assignee?.name || t('CHAT_LIST.TMK.UNASSIGNED')"
      >
        <span
          class="i-lucide-user-round size-3.5 shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
        <span class="truncate">{{
          assignee?.name || t('CHAT_LIST.TMK.UNASSIGNED')
        }}</span>
      </div>
      <div
        class="flex items-center gap-1.5 text-[11px] text-n-slate-10"
        :title="`${formatDate(conversation.created_at, true)} · ${t('CHAT_LIST.TMK.DAY_TIMEZONE')}`"
      >
        <span
          class="i-lucide-calendar-days size-3.5 shrink-0"
          aria-hidden="true"
        />
        <span>{{ t('CHAT_LIST.TMK.CREATED_ON') }}</span>
        <time
          data-testid="kanban-created-at"
          :datetime="parseDate(conversation.created_at)?.toISOString()"
          class="ml-auto tabular-nums"
          >{{ formatDate(conversation.created_at) }}</time
        >
      </div>
      <div
        class="flex items-center justify-between gap-2 text-[10px] text-n-slate-10"
      >
        <span>{{ t('CHAT_LIST.TMK.LAST_ACTIVITY') }}</span>
        <span :title="formatDate(conversation.last_activity_at, true)">
          {{ relativeTime(conversation.last_activity_at) }}
        </span>
      </div>
    </div>
  </div>
</template>
